import axios from 'axios';
import { message } from 'ant-design-vue';
import tokenService from '@/utils/tokenService';
import { handleAliyunTokenExpired, ALIYUN_TOKEN_EXPIRED_CODE } from '@/utils/aliyunRebind';
import logger, {
  getSessionId,
  applyTracingHeaders,
  generateTraceId,
} from '@utils/logger';

const HTTP = 'HTTP';

// ⚠️ 不要静态 import '@/router'：router 会在 beforeEach 里 import store / api，
// 而 api 又 import 本文件，形成 router ↔ myRequest 循环依赖，导致 Vite HMR 报
// "failed to apply HMR as it's within a circular import" 并整页 reload，
// 循环里的组件（如播放页）还可能在半更新模块图中抛错、使路由跳转失败。
// 这里改为按需动态加载，循环即被打破。
let routerModulePromise = null;
function getRouter() {
  if (!routerModulePromise) {
    routerModulePromise = import('@/router').then((mod) => mod.default);
  }
  return routerModulePromise;
}

/** 未登录时统一跳回首页并打开登录弹窗 */
function redirectToLogin() {
  getRouter()
    .then((router) => {
      try {
        sessionStorage.setItem('redirectAfterLogin', router.currentRoute.value.fullPath);
      } catch (error) {
        // sessionStorage 不可用时忽略重定向目标记录
      }
      router.push({ path: '/', query: { auth: 'login' } });
    })
    .catch(() => {});
}

function traceIdFrom(config) {
  if (!config) return undefined;
  return config.metadata?.traceId || config.headers?.['X-Trace-Id'];
}

function httpLog(level, message, data, config) {
  logger[level](HTTP, message, data, traceIdFrom(config));
}

// 是否正在刷新token
let isRefreshing = false;
// 等待刷新token的请求队列
let requestsQueue = [];

// 🔥 防抖机制：避免多个错误提示同时显示导致UI卡顿
let errorMessageTimer = null;
let lastErrorMessageTime = 0;
const ERROR_MESSAGE_DEBOUNCE = 1000; // 1秒内只显示一次错误提示

// 执行请求队列中的请求
const processRequestsQueue = (token) => {
  requestsQueue.forEach(({ config, resolve, reject }) => {
    config.headers['Authorization'] = `Bearer ${token}`;
    axios(config).then(resolve).catch(reject);
  });
  requestsQueue = [];
};

// 创建axios实例
// 使用环境变量配置API基础URL，添加调试信息
const getApiBaseUrl = () => {
  const envUrl = import.meta.env.VITE_APP_API_BASE_URL;
  let resolvedUrl;

  if (envUrl === '') {
    resolvedUrl = '';
  } else if (envUrl && envUrl !== 'undefined') {
    resolvedUrl = envUrl;
  } else {
    const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    resolvedUrl = isLocalhost ? 'http://localhost:8080' : '';
  }

  logger.debug(HTTP, 'API baseURL 已解析', {
    envUrl,
    resolvedUrl: resolvedUrl || '相对路径（当前域名）',
  });
  return resolvedUrl;
};

const myRequest = axios.create({
  baseURL: getApiBaseUrl(),
  timeout: import.meta.env.VITE_APP_API_TIMEOUT || 360000,
  withCredentials: true  // 允许请求携带cookie，关键用于session认证
});

// 请求拦截器
myRequest.interceptors.request.use(
  config => {
    const traceId = generateTraceId();
    applyTracingHeaders(config, getSessionId(), traceId);

    const accessToken = tokenService.getAccessToken();
    if (accessToken) {
      config.headers['Authorization'] = `Bearer ${accessToken}`;
    }

    if (config.data && !(config.data instanceof FormData)) {
      config.headers['Content-Type'] = 'application/json';
    }

    if (config.url === '/api/book/upload' && config.data instanceof FormData) {
      config.headers['Content-Type'] = 'multipart/form-data';
      try {
        const fields = [];
        for (const [key, value] of config.data.entries()) {
          if (value instanceof File) {
            fields.push(`${key}:文件(${value.name}, ${Math.round(value.size / 1024)}KB)`);
          } else {
            fields.push(`${key}:${value}`);
          }
        }
        httpLog('debug', '书籍上传 FormData 字段', fields.join(', '), config);
      } catch (e) {
        httpLog('error', '读取 FormData 失败', e, config);
      }
    }

    httpLog('debug', `${config.method?.toUpperCase()} ${config.url}`, {
      params: config.params,
      hasBody: !!config.data,
      isFormData: config.data instanceof FormData,
    }, config);

    return config;
  },
  error => {
    httpLog('error', '请求发送失败', error);
    return Promise.reject(error);
  }
);

// 响应拦截器
myRequest.interceptors.response.use(
  response => {
    const config = response.config;
    httpLog('debug', `响应 ${response.status} ${config.url}`, response.data, config);

    // 分片上传API的特殊处理
    if (config.url.includes('/api/book/chunk')) {
      if (response.data && response.data.code === 0) {
        httpLog('debug', '分片上传响应', response.data, config);
        return response.data.data;
      }
    }
    
    // 从响应中获取数据
    const res = response.data;
    
    // 特殊处理：如果响应没有code字段，说明是直接返回的数据（如OneDrive直链API）
    if (res.code === undefined) {
      httpLog('debug', '无 code 字段，直接返回数据', undefined, config);
      return res;
    }

    if (res.code !== 0) {
      httpLog('warn', `业务 code 非 0: ${res.code}`, res.message, config);

      if (res.message && res.message.includes('成功')) {
        httpLog('debug', '消息含「成功」但 code 非 0，按成功处理', undefined, config);
        return res.data;
      }

      if (res.code === 1001) {
        httpLog('debug', '1001 无音频，不弹错误', undefined, config);
        return res;
      }

      const url = config.url || '';
      if (res.code === 40404 && url.includes('/lyrics')) {
        httpLog('debug', '歌词 40404，交由前端处理', undefined, config);
        return Promise.reject(new Error(res.message || '歌曲不存在'));
      }

      if (res.code === 40403 && url.includes('/lyrics')) {
        httpLog('debug', '歌词 40403，交由前端处理', undefined, config);
        return Promise.reject(new Error(res.message || '无权访问该歌曲'));
      }

      if (url.includes('/api/douyin/parse-only')) {
        httpLog('debug', '抖音解析错误，交由前端处理', undefined, config);
        return Promise.reject(new Error(res.message || '解析失败'));
      }

      // 静默请求（config.silent）：后台同步类接口不弹全局错误、不触发登录跳转
      if (config.silent) {
        httpLog('debug', '静默请求失败，交由调用方处理', res.message, config);
        return Promise.reject(new Error(res.message || '请求出错'));
      }

      if (url.includes('/api/onedrive/storage-quota') &&
          res.message &&
          (res.message.includes('无法获取存储容量') || res.message === '无法获取存储容量')) {
        httpLog('debug', 'OneDrive 未连接，交由前端处理', undefined, config);
        return Promise.reject(new Error(res.message || '无法获取存储容量'));
      }

      message.error(res.message || '请求出错');

      if (res.code === 40100) {
        httpLog('info', '用户未登录，打开登录弹窗', undefined, config);
        tokenService.clearTokens();

        // 保存重定向目标 + 跳转首页并打开登录弹窗
        redirectToLogin();
      }
      
      return Promise.reject(new Error(res.message || '请求出错'));
    }
    
    httpLog('debug', '业务 code=0，请求成功', undefined, config);
    return res.data;
  },
  async error => {
    const config = error.config;

    if (axios.isCancel(error)) {
      httpLog('debug', '请求被取消', undefined, config);
      return Promise.reject(error);
    }

    httpLog('error', '请求失败', error, config);

    if (error.response) {
      httpLog('error', `HTTP ${error.response.status}`, error.response.data, config);

      const originalRequest = error.config;

      if (error.response.status === 401) {
        httpLog('info', '401 未授权，尝试刷新 token', undefined, config);

        const refreshToken = tokenService.getRefreshToken();
        if (!refreshToken) {
          httpLog('info', '无 refresh token，清除登录状态', undefined, config);
          tokenService.clearTokens();

          // 保存重定向目标 + 跳转首页并打开登录弹窗
          redirectToLogin();

          return Promise.reject(error);
        }
        
        // 如果已经在刷新token，将请求加入队列
        if (isRefreshing) {
          httpLog('debug', '正在刷新 token，请求入队', undefined, config);
          return new Promise((resolve, reject) => {
            requestsQueue.push({
              config: originalRequest,
              resolve,
              reject
            });
          });
        }
        
        // 开始刷新token
        isRefreshing = true;
        
        try {
          httpLog('debug', '开始刷新 token', undefined, config);
          const response = await axios({
            method: 'post',
            url: `${import.meta.env.VITE_APP_API_BASE_URL}/api/user/refresh-token`,
            data: { refreshToken },
            withCredentials: true,
            headers: {
              'X-Session-Id': getSessionId(),
              'X-Trace-Id': generateTraceId(),
            },
          });
          
          const res = response.data;
          
          if (res.code === 0 && res.data && res.data.accessToken) {
            httpLog('info', '刷新 token 成功', undefined, config);
            const newToken = res.data.accessToken;
            
            // 保存新的访问令牌
            tokenService.saveAccessToken(newToken);
            
            // 更新当前请求的认证头
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            
            // 处理队列中的请求
            processRequestsQueue(newToken);
            
            // 重试当前请求
            return axios(originalRequest);
          } else {
            httpLog('error', '刷新 token 失败', res, config);
            // 刷新令牌失败，清除登录状态
            tokenService.clearTokens();

            // 保存重定向目标 + 跳转首页并打开登录弹窗
            redirectToLogin();

            return Promise.reject(new Error(res.message || '刷新令牌失败'));
          }
        } catch (refreshError) {
          httpLog('error', '刷新 token 异常', refreshError, config);
          // 刷新令牌出错，清除登录状态
          tokenService.clearTokens();

          // 保存重定向目标 + 跳转首页并打开登录弹窗
          redirectToLogin();

          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }
    } else if (error.request) {
      httpLog('error', '未收到响应', error.request, config);
    } else {
      httpLog('error', '请求配置错误', error.message, config);
    }

    if (error.response && error.response.data) {
      const errData = error.response.data;

      if (errData.message && errData.message.includes('成功')) {
        httpLog('debug', '错误响应含成功信息，按成功处理', undefined, config);
        message.success(errData.message || '操作成功');
        return errData.data;
      }
    }
    
    // 特殊处理：分享页云盘直链探测的 400 错误不弹窗（由分享页自行回退 stream）
    if (error.response && error.response.status === 400 && error.response.data) {
      const errData = error.response.data;
      const errMsg = errData.message || errData.data?.message || errData.error || '';
      const errCode = errData.code;

      if (errCode === ALIYUN_TOKEN_EXPIRED_CODE || errMsg.includes('expired refresh_token') || errMsg.includes('授权已过期')) {
        handleAliyunTokenExpired({ code: errCode, message: errMsg, response: error.response });
        const expiredError = new Error(errMsg || '阿里云盘授权已过期，请重新扫码绑定');
        expiredError.code = ALIYUN_TOKEN_EXPIRED_CODE;
        return Promise.reject(expiredError);
      }

      if (errMsg.includes('不是OneDrive存储') ||
          errMsg.includes('本地存储') ||
          errMsg.includes('流式接口')) {
        httpLog('debug', '分享云盘存储类型探测错误，不弹窗', undefined, config);
        return Promise.reject(error);
      }
    }

    // BusinessException 等：HTTP 4xx/5xx 响应体带 message，勿用 axios 默认文案
    if (error.response?.data) {
      const errData = error.response.data;
      const errMsg = (
        (typeof errData.message === 'string' ? errData.message : '') ||
        (typeof errData.data?.message === 'string' ? errData.data.message : '') ||
        ''
      ).trim();
      if (errMsg && !errMsg.includes('成功')) {
        const apiError = new Error(errMsg);
        if (errData.code != null) {
          apiError.code = errData.code;
        }
        httpLog('debug', '使用后端语义化错误信息', errMsg, config);
        return Promise.reject(apiError);
      }
    }
    
    // 检查是否需要显示网络错误提示
    // 对于某些不需要登录的API，在服务不可用时不显示错误提示
    const shouldShowNetworkError = (error, config) => {
      // 静默请求由调用方自行降级处理，不弹全局提示
      if (config?.silent) return false;
      const url = config?.url || '';
      const noErrorApis = [
        // '/api/music/hot-singers',      // 🚫 已移除：不再使用热门歌手功能
        '/api/music/search',           // 广场音乐搜索（无需登录）
        '/api/music/singer',           // 歌手歌曲（无需登录）
        '/api/user/login',             // 登录API（登录失败时不显示网络错误）
        '/api/user/refresh-token',     // 刷新token API（token失效时不显示错误）
        '/api/douyin/parse-only',      // 抖音解析API（让前端组件处理错误显示）
        '/api/music/onedrive-direct-url', // 播放直链（兼容路径）
        '/api/music/playback-direct-url', // 播放直链（OneDrive/阿里云盘）
        '/api/music/aliyun-video-preview', // 阿里云盘视频 HLS 播放信息（非阿里云返回400，静默回退）
        '/api/video/aliyun-video-preview', // 图集内视频 HLS 播放信息
        '/api/music/onedrive-stream',  // OneDrive流式播放API（播放失败时不显示错误）
        '/api/music/share/',           // 分享页直链/流式/封面/图集（访客场景静默失败）
        '/api/aliyun/media/cover-url', // 未登录时封面探测失败（分享页走 share/cover）
        '/api/aliyun/media/create-upload', // 过期 token 由 aliyunRebind 引导重绑
        '/api/aliyun/media/cover-complete',
        '/api/aliyun/media/complete',
        '/api/onedrive/status',        // OneDrive状态检查（连接失败时不显示错误）
        '/api/onedrive/auth-url',      // OneDrive授权URL（获取失败时不显示错误）
        '/api/onedrive/upload-auth',   // OneDrive上传授权（获取失败时不显示错误）
        '/api/onedrive/stream-url'     // OneDrive流式URL（获取失败时不显示错误）
      ];
      
      // 🔥 关键修复：对于登录API，完全跳过所有类型的网络错误提示（不仅仅是 ERR_NETWORK）
      // 包括：ERR_NETWORK, ECONNABORTED (超时), ECONNRESET (连接重置), ETIMEDOUT 等
      if (noErrorApis.some(api => url.includes(api))) {
        httpLog('debug', `API ${url} 跳过网络错误提示`, undefined, config);
        return false;
      }
      
      // 如果是网络连接错误（ERR_NETWORK, ECONNABORTED, ECONNRESET, ETIMEDOUT 等）
      const isNetworkError = error.code === 'ERR_NETWORK' || 
                            error.code === 'ECONNABORTED' || 
                            error.code === 'ECONNRESET' || 
                            error.code === 'ETIMEDOUT' ||
                            error.message === 'Network Error' ||
                            error.message?.includes('timeout') ||
                            error.message?.includes('network');
      
      // 对于网络错误，检查是否需要显示
      if (isNetworkError) {
        // 如果是不需要显示错误的API，返回false
        if (noErrorApis.some(api => url.includes(api))) {
          httpLog('debug', `API ${url} 跳过网络错误提示`, undefined, config);
          return false;
        }
      }
      
      return true;
    };
    
    // 特殊处理：1001状态码（无音频）不显示错误提示
    if (error.response && error.response.data && error.response.data.code === 1001) {
      httpLog('debug', '1001 无音频，不弹错误', undefined, config);
      return Promise.reject(error);
    }
    
    // 根据配置决定是否显示网络错误提示
    if (shouldShowNetworkError(error, error.config)) {
      // 🔥 使用防抖机制避免多个错误提示同时显示导致UI卡顿
      const now = Date.now();
      if (now - lastErrorMessageTime > ERROR_MESSAGE_DEBOUNCE) {
        // 清除之前的定时器
        if (errorMessageTimer) {
          clearTimeout(errorMessageTimer);
        }
        
        // 🔥 使用 nextTick 将 message.error 改为异步调用，避免阻塞UI渲染
        errorMessageTimer = setTimeout(() => {
          httpLog('error', '网络错误', error, config);
          const errorInfo = {
            code: error.code || 'N/A',
            message: error.message || 'N/A',
            name: error.name || 'N/A',
            url: config?.url || 'N/A',
            method: config?.method?.toUpperCase() || 'N/A',
            baseURL: config?.baseURL || 'N/A',
            timeout: config?.timeout || 'N/A',
            status: error.response?.status || 'N/A',
            statusText: error.response?.statusText || 'N/A',
            stack: error.stack || 'N/A',
          };
          httpLog('error', '网络错误详情', errorInfo, config);
          lastErrorMessageTime = Date.now();
        }, 0);
      } else {
        httpLog('debug', '错误提示防抖，跳过', undefined, config);
      }
    } else {
      httpLog('debug', '跳过网络错误提示', undefined, config);
    }
    
    return Promise.reject(error);
  }
);

// 导出自定义请求实例
export default myRequest;
// 如果需要原生 axios，可以这样导出
export { axios as myRawAxios }; 